import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import plur from 'plur';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { AssistantContextSubType } from '../types.js';
import { useChatbot } from '../hooks/useChatbot.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import Select from '@fabric-app/ui/components/Select';
import { CloseIcon } from '@fabric-app/icons/icons';
import { useAuthStatus } from '@fabric-app/compat';
import { ExtraPanelSidebar } from '../../resource-detail/components/ExpandedResource/ExtraPanel/Sidebar.js';
import { IconSidePanelFullScreen } from '@fabric-app/icons/svg';
import { useAssistantStore } from '../stores/assistantStore.js';

const AssistantInnerHeader = styled(Flex).attrs((p) => {
  return {
    justifyContent: "space-between",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "AssistantHeader__AssistantInnerHeader",
  componentId: "sc-1e02a120-0"
})([
  `width:100%;max-width:800px;margin:0 auto;`
]);
const AssistantHeaderRoot = styled(Flex).attrs((p) => {
  return {
    justifyContent: "space-between",
    alignItems: "center",
    ...p,
    children: /* @__PURE__ */ jsx(AssistantInnerHeader, {
      children: p.children
    })
  };
}).withConfig({
  displayName: "AssistantHeader__AssistantHeaderRoot",
  componentId: "sc-1e02a120-1"
})([
  `flex-shrink:0;padding:0.75rem;border-bottom:1px solid rgba(`,
  `,0.1);`
], cssVar["color-bg-contrast-reverse-rgb"]);
const AssistantHeaderLeft = styled(Flex).attrs((p) => {
  return {
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "AssistantHeader__AssistantHeaderLeft",
  componentId: "sc-1e02a120-2"
})([
  `gap:0.375rem;`
]);
const AssistantHeaderRight = styled(Flex).attrs((p) => {
  return {
    alignItems: "center",
    justifyContent: "flex-end",
    ...p
  };
}).withConfig({
  displayName: "AssistantHeader__AssistantHeaderRight",
  componentId: "sc-1e02a120-3"
})([
  `gap:0.625rem;`
]);
const Root = AssistantHeaderRoot;
const Left = AssistantHeaderLeft;
const AssistantButton = ExtraPanelSidebar.Button;
const AssistantButtonIcon = styled(AssistantButton).attrs((p) => {
  return {
    square: "shape",
    ...p
  };
}).withConfig({
  displayName: "AssistantHeader__AssistantButtonIcon",
  componentId: "sc-1e02a120-4"
})([
  `width:30px;height:30px;`
]);
const SelectionBadge = styled.span.withConfig({
  displayName: "AssistantHeader__SelectionBadge",
  componentId: "sc-1e02a120-5"
})([
  `border:1px dashed `,
  `;border-radius:`,
  `;padding:3px 10px;font-size:0.813rem;font-weight:500;line-height:1.5rem;display:flex;align-items:center;color:`,
  `;[data-assistant-fullscreen='true'] &{height:40px;min-width:40px;padding:0 0.75rem;}`
], cssVar["color-border-primary"], cssVar["radius-input"], cssVar["color-text-primary"]);
const ContextSelector = /* @__PURE__ */ forwardRef((props, ref) => {
  const { activeContext, setActiveContext, resetSelection, contexts } = useChatbot();
  const { chatAssistantFullscreen } = useAssistantStore();
  const authStatus = useAuthStatus();
  if (activeContext.subType === AssistantContextSubType.Selection && Array.isArray(activeContext.value) && activeContext.value.length > 0) {
    return /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsxs(SelectionBadge, {
          children: [
            activeContext.value.length,
            " ",
            plur("source", activeContext.value.length)
          ]
        }),
        /* @__PURE__ */ jsx(AssistantButtonIcon, {
          onClick: resetSelection,
          children: /* @__PURE__ */ jsx(CloseIcon, {
            size: 15
          })
        })
      ]
    });
  }
  return /* @__PURE__ */ jsx(Select, {
    value: activeContext.subType,
    onChange: (value) => {
      const nextOption = contexts.find((c) => c.subType === value);
      if (nextOption) {
        setActiveContext(nextOption);
      }
    },
    disabled: contexts.length === 1,
    ref,
    ...props,
    triggerProps: {
      size: chatAssistantFullscreen ? void 0 : "xs",
      variant: "outline-quaternary",
      disabled: contexts.length === 1,
      style: chatAssistantFullscreen ? {
        color: "white !important"
      } : {
        fontSize: 13
      },
      ...props.triggerProps
    },
    children: contexts.filter((context) => {
      if (authStatus !== "authenticated" && context.subType === AssistantContextSubType.Everything) {
        return false;
      }
      return true;
    }).map((context) => /* @__PURE__ */ jsx(Select.ItemOption, {
      value: context.subType,
      children: context.subType === AssistantContextSubType.Everything ? "everything" : `this ${context.subType}`
    }, context.subType))
  });
});
ContextSelector.displayName = "ContextSelector";
const ResetButton = /* @__PURE__ */ forwardRef((props, ref) => {
  const { clearMessages } = useChatbot();
  return /* @__PURE__ */ jsx(AssistantButton, {
    onClick: clearMessages,
    ref,
    ...props,
    children: "Clear"
  });
});
ResetButton.displayName = "ResetButton";
const FullScreenButton = styled(AssistantButton).attrs((p) => {
  return {
    ...p,
    children: /* @__PURE__ */ jsx(IconSidePanelFullScreen, {})
  };
}).withConfig({
  displayName: "AssistantHeader__FullScreenButton",
  componentId: "sc-1e02a120-6"
})([
  ``
]);
const AssistantHeader = {
  Root,
  Left,
  Right: AssistantHeaderRight,
  ContextSelector,
  ResetButton,
  FullScreenButton
};

export { AssistantHeader, AssistantHeaderLeft, AssistantHeaderRoot, ContextSelector, FullScreenButton, Left, ResetButton, Root };
//# sourceMappingURL=AssistantHeader.js.map
